import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useGT } from "gt-react";
import { Search, FileText, Server, Globe } from "lucide-react";
import {
  useServersStore,
  useAppStore,
  selectServer,
  selectQuery,
} from "../store";
import * as db from "../persistence";
import type { StoredQuery } from "../persistence";

const MAX_RESULTS = 20;

interface QueryWithServer {
  query: StoredQuery;
  serverName: string;
}

function firstLine(text: string): string {
  const line = text.split("\n").find((l) => l.trim().length > 0) ?? "";
  return line.length > 80 ? line.slice(0, 80) + "..." : line;
}

function queryPreview(query: StoredQuery): string {
  if (query.requestType === "rest") {
    return `${query.restMethod ?? "GET"} ${query.restPath ?? "/"}`;
  }
  return firstLine(query.queryText);
}

function matchesSearch(item: QueryWithServer, term: string): boolean {
  if (!term) return true;
  const lower = term.toLowerCase();
  const name = item.query.name.toLowerCase();
  const text = queryPreview(item.query).toLowerCase();
  const server = item.serverName.toLowerCase();
  return name.includes(lower) || text.includes(lower) || server.includes(lower);
}

export function QuickOpen() {
  const t = useGT();
  const setShowQuickOpen = useAppStore((s) => s.setShowQuickOpen);
  const servers = useServersStore((s) => s.servers);

  const [search, setSearch] = useState("");
  const [allQueries, setAllQueries] = useState<QueryWithServer[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Load all queries on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const queries = await db.listAllQueries();
      if (cancelled) return;

      const serverMap = new Map(servers.map((s) => [s.id, s.name]));
      const items: QueryWithServer[] = queries.map((q) => ({
        query: q,
        serverName: serverMap.get(q.serverId) ?? t("Unknown"),
      }));
      setAllQueries(items);
    }
    load();
    return () => { cancelled = true; };
  }, [servers, t]);

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    return allQueries.filter((item) => matchesSearch(item, search)).slice(0, MAX_RESULTS);
  }, [allQueries, search]);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  const close = useCallback(() => {
    setShowQuickOpen(false);
  }, [setShowQuickOpen]);

  const handleSelect = useCallback(
    async (item: QueryWithServer) => {
      close();
      await selectServer(item.query.serverId);
      selectQuery(item.query);
    },
    [close],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((i) => Math.max(i - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filtered[selectedIndex]) {
            handleSelect(filtered[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          close();
          break;
      }
    },
    [filtered, selectedIndex, handleSelect, close],
  );

  // Scroll selected item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const item = list.children[selectedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm pt-[15vh]"
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-border bg-surface-raised shadow-2xl"
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Search size={14} className="shrink-0 text-zinc-500" />
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Search queries...")}
            className="w-full bg-transparent text-sm text-zinc-200 outline-none placeholder:text-zinc-600"
          />
          <kbd className="shrink-0 rounded bg-surface-overlay px-1.5 py-0.5 text-[10px] font-mono text-zinc-500">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-80 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-zinc-600">
              {allQueries.length === 0 ? t("No queries yet") : t("No matches")}
            </div>
          )}
          {filtered.map((item, i) => (
            <button
              key={item.query.id}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setSelectedIndex(i)}
              className={`flex w-full items-start gap-2 px-3 py-2 text-left transition-colors ${
                i === selectedIndex
                  ? "bg-surface-overlay/60"
                  : "hover:bg-surface-overlay/30"
              }`}
            >
              {item.query.requestType === "rest" ? (
                <Globe size={14} className="mt-0.5 shrink-0 text-success" />
              ) : (
                <FileText size={14} className="mt-0.5 shrink-0 text-warning" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm text-zinc-200">
                    {item.query.name}
                  </span>
                  <span className="flex shrink-0 items-center gap-1 text-[11px] text-zinc-600">
                    <Server size={10} />
                    {item.serverName}
                  </span>
                </div>
                {(item.query.queryText.trim() || item.query.requestType === "rest") && (
                  <div className="mt-0.5 truncate font-mono text-[11px] text-zinc-600">
                    {queryPreview(item.query)}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
